import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AgentListingReviewSummary,
  type ListingReviewStatus,
  approveAgentListingReview,
  fetchAgentListingReviews,
  rejectAgentListingReview,
} from "../../api/admin-agents";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  Pagination,
  Td,
  Th,
  fmtDate,
  tableStyles,
} from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";

const PAGE_SIZE = 20;
const REVIEW_STATUSES: ListingReviewStatus[] = [
  "submitted",
  "human_review",
  "auto_check_failed",
  "approved",
  "rejected",
  "withdrawn",
];
const DECIDABLE_STATUSES = new Set<ListingReviewStatus>([
  "submitted",
  "human_review",
  "auto_check_failed",
]);

type ReviewAction = {
  readonly kind: "approve" | "reject";
  readonly item: AgentListingReviewSummary;
};

export function AgentListingReviewsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ListingReviewStatus>("submitted");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AgentListingReviewSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ReviewAction | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAgentListingReviews({
        status,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("agent_reviews.failed_to_load"),
      );
    } finally {
      setLoading(false);
    }
  }, [status, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirm = useCallback(
    async (notes: string): Promise<void> => {
      if (!action) return;
      setSaving(true);
      setError(null);
      try {
        if (action.kind === "approve") {
          await approveAgentListingReview(
            action.item.agent_id,
            action.item.version,
            notes || undefined,
          );
        } else {
          await rejectAgentListingReview(
            action.item.agent_id,
            action.item.version,
            notes,
          );
        }
        setAction(null);
        await load();
      } catch (e) {
        setError(
          e instanceof Error ? e.message : t("agent_reviews.action_failed"),
        );
      } finally {
        setSaving(false);
      }
    },
    [action, load],
  );

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title={t("agent_reviews.title")}
        description={t("agent_reviews.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar
        status={status}
        onStatusChange={(nextStatus) => {
          setStatus(nextStatus);
          setOffset(0);
        }}
        onRefresh={load}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loadingText}>{t("common.loading")}</p>
      ) : (
        <>
          <ReviewsTable
            items={items}
            saving={saving}
            onAction={setAction}
            onViewAgent={(id) => navigate(`/governance/agents/${id}`)}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
          />
        </>
      )}
      <ConfirmDialog
        open={action !== null}
        title={
          action?.kind === "approve"
            ? t("agent_reviews.approve_title")
            : t("agent_reviews.reject_title")
        }
        description={
          action?.kind === "approve"
            ? t("agent_reviews.approve_desc")
            : t("agent_reviews.reject_desc")
        }
        reasonRequired={action?.kind === "reject"}
        reasonMinLength={10}
        onConfirm={(reason) => {
          void handleConfirm(reason);
        }}
        onCancel={() => setAction(null)}
      />
    </div>
  );
}

function FilterBar(props: {
  readonly status: ListingReviewStatus;
  readonly onStatusChange: (status: ListingReviewStatus) => void;
  readonly onRefresh: () => void;
}): React.JSX.Element {
  return (
    <div style={s.filterRow}>
      <select
        value={props.status}
        onChange={(e) =>
          props.onStatusChange(e.target.value as ListingReviewStatus)
        }
        style={s.filterSelect}
        aria-label={t("agent_reviews.status_filter")}
      >
        {REVIEW_STATUSES.map((status) => (
          <option key={status} value={status}>
            {statusLabel(status)}
          </option>
        ))}
      </select>
      <button type="button" onClick={props.onRefresh} style={s.filterBtn}>
        {t("common.refresh")}
      </button>
    </div>
  );
}

function ReviewsTable(props: {
  readonly items: ReadonlyArray<AgentListingReviewSummary>;
  readonly saving: boolean;
  readonly onAction: (action: ReviewAction) => void;
  readonly onViewAgent: (id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("agent_reviews.empty")}</p>;
  }
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <Th>{t("agent_reviews.col_agent")}</Th>
          <Th>{t("agent_reviews.col_creator")}</Th>
          <Th>{t("agent_reviews.col_version")}</Th>
          <Th>{t("agent_reviews.col_status")}</Th>
          <Th>{t("agent_reviews.col_reasons")}</Th>
          <Th>{t("agent_reviews.col_submitted")}</Th>
          <Th>{t("common.action")}</Th>
        </tr>
      </thead>
      <tbody>
        {props.items.map((item) => (
          <ReviewRow
            key={item.review_id}
            item={item}
            saving={props.saving}
            onAction={props.onAction}
            onViewAgent={props.onViewAgent}
          />
        ))}
      </tbody>
    </table>
  );
}

function ReviewRow(props: {
  readonly item: AgentListingReviewSummary;
  readonly saving: boolean;
  readonly onAction: (action: ReviewAction) => void;
  readonly onViewAgent: (id: string) => void;
}): React.JSX.Element {
  const item = props.item;
  const canDecide = DECIDABLE_STATUSES.has(item.review_status);
  return (
    <tr>
      <Td>
        <button
          type="button"
          onClick={() => props.onViewAgent(item.agent_id)}
          style={s.linkButton}
        >
          {item.agent_name}
        </button>
      </Td>
      <Td>{item.creator_name || item.creator_id}</Td>
      <Td>{item.version}</Td>
      <Td>
        <span style={s.badge}>{statusLabel(item.review_status)}</span>
      </Td>
      <Td>{item.required_review_reasons.join(", ") || "-"}</Td>
      <Td>{fmtDate(item.submitted_at)}</Td>
      <Td>
        <div style={s.actionRow}>
          <button
            type="button"
            disabled={!canDecide || props.saving}
            onClick={() => props.onAction({ kind: "approve", item })}
            style={s.approveBtn}
          >
            {t("agent_reviews.approve")}
          </button>
          <button
            type="button"
            disabled={!canDecide || props.saving}
            onClick={() => props.onAction({ kind: "reject", item })}
            style={s.rejectBtn}
          >
            {t("agent_reviews.reject")}
          </button>
        </div>
      </Td>
    </tr>
  );
}

function statusLabel(status: ListingReviewStatus): string {
  return t(`agent_reviews.status.${status}`);
}

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  filterSelect: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    background: "var(--color-bg)",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    background: "#eef2ff",
    color: "#3730a3",
  },
  linkButton: {
    border: "none",
    padding: 0,
    background: "transparent",
    color: "var(--color-primary)",
    cursor: "pointer",
    fontSize: 13,
  },
  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  approveBtn: {
    padding: "5px 10px",
    borderRadius: 6,
    border: "1px solid #86efac",
    background: "#dcfce7",
    color: "#166534",
    fontSize: 12,
  },
  rejectBtn: {
    padding: "5px 10px",
    borderRadius: 6,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    fontSize: 12,
  },
  empty: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    padding: 24,
    textAlign: "center",
  },
};
